"use client";

/**
 * AppPushBridge — a ponte das notificações DENTRO do aplicativo instalado.
 *
 * Sem interface. Montado uma vez na raiz, ao lado do registro do service
 * worker, e no navegador não faz absolutamente nada.
 *
 * O que faz dentro do app:
 *  - mantém o token do aparelho atualizado no servidor. O Firebase troca o
 *    token sozinho (reinstalação, limpeza de dados, restauração de backup), e
 *    um token velho é um aviso que nunca chega;
 *  - abre a tela certa quando a pessoa toca no aviso;
 *  - mostra o aviso que chega com o app ABERTO, que o Android não desenha.
 *
 * O que NÃO faz: pedir a permissão. Quem pede é o botão "Ativar
 * notificações" de Minha conta, onde a pessoa escolheu — pedir na abertura,
 * talvez ainda na tela de entrar, é o jeito certo de levar um "não" para
 * sempre. Aqui só registramos quando a permissão JÁ foi dada.
 */
import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

import { saveAppPushToken } from "@/lib/data/push";
import {
  isNativeApp,
  nativePushPermission,
  nativePushToken,
  onNativePushReceived,
  onNativePushTapped,
} from "@/lib/pwa/native-push";

export default function AppPushBridge() {
  const router = useRouter();

  useEffect(() => {
    if (!isNativeApp()) return;
    let cancelled = false;

    void onNativePushTapped((url) => {
      router.push(url);
    });

    void onNativePushReceived((n) => {
      if (!n.title && !n.body) return;
      toast(n.title ?? "Aviso", {
        description: n.body,
        ...(n.url
          ? { action: { label: "Abrir", onClick: () => router.push(n.url!) } }
          : {}),
      });
    });

    void (async () => {
      if ((await nativePushPermission()) !== "granted") return;
      const token = await nativePushToken();
      if (cancelled || !token) return;
      // Sem sessão (tela de entrar) a gravação é recusada — e tudo bem: na
      // próxima abertura, já dentro, ela acontece.
      await saveAppPushToken(token, "app").catch(() => {});
    })();

    return () => {
      cancelled = true;
    };
  }, [router]);

  return null;
}
